import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, FileText, CheckCircle, XCircle, Clock, TrendingUp, Award } from 'lucide-react';

const GrantApplicationAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    totalApplications: 0,
    approvedApplications: 0,
    pendingApplications: 0,
    rejectedApplications: 0,
    totalAmountRequested: 0,
    totalAmountApproved: 0,
    averageProcessingTime: 0,
    approvalRate: 0,
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const loadAnalytics = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setAnalytics({
        totalApplications: 5,
        approvedApplications: 3,
        pendingApplications: 2,
        rejectedApplications: 1,
        totalAmountRequested: 2450000,
        totalAmountApproved: 1875000,
        averageProcessingTime: 4.2,
        approvalRate: 71.5,
      });
      setRecentApplications([
        {
          id: 1,
          applicant: 'Sarah Johnson',
          business: 'EcoTech Solutions',
          amount: 50000,
          status: 'approved',
          submittedDate: '2024-01-15',
          processedDate: '2024-01-18',
        },
        {
          id: 2,
          applicant: 'Michael Chen',
          business: 'Digital Innovations',
          amount: 75000,
          status: 'pending',
          submittedDate: '2024-01-16',
          processedDate: null,
        },
        {
          id: 3,
          applicant: 'Emily Rodriguez',
          business: 'HealthTech Pro',
          amount: 100000,
          status: 'rejected',
          submittedDate: '2024-01-14',
          processedDate: '2024-01-17',
        },
        {
          id: 4,
          applicant: 'David Kim',
          business: 'Green Energy Co',
          amount: 60000,
          status: 'approved',
          submittedDate: '2024-01-13',
          processedDate: '2024-01-16',
        },
        {
          id: 5,
          applicant: 'Lisa Wang',
          business: 'Smart Retail',
          amount: 45000,
          status: 'pending',
          submittedDate: '2024-01-17',
          processedDate: null,
        },
      ]);
      setIsLoading(false);
    };
    loadAnalytics();
    const interval = setInterval(loadAnalytics, 60000);
    const darkListener = e => setDarkMode(e.matches);
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', darkListener);
    return () => {
      clearInterval(interval);
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', darkListener);
    };
  }, []);

  const statusConfig = {
    approved: {
      icon: CheckCircle,
      bg: 'bg-success/10',
      text: 'text-success',
    },
    pending: {
      icon: Clock,
      bg: 'bg-accent/10',
      text: 'text-accent',
    },
    rejected: {
      icon: XCircle,
      bg: 'bg-error/10',
      text: 'text-error',
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className="rounded-xl border border-accent/20 bg-bg-card p-6 shadow-sm dark:border-accent/40 dark:bg-nav"
            >
              <div className="animate-pulse">
                <div className="mb-4 flex items-center justify-between">
                  <div className="h-10 w-10 rounded-lg bg-accent/10 dark:bg-accent/90"></div>
                  <div className="h-4 w-16 rounded bg-accent/10 dark:bg-accent/90"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-6 w-24 rounded bg-accent/10 dark:bg-accent/90"></div>
                  <div className="h-8 w-32 rounded bg-accent/10 dark:bg-accent/90"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="rounded-xl border border-cyan-100 bg-white p-4 shadow-sm dark:border-cyan-900 dark:bg-neutral-900 md:p-6">
          <div className="animate-pulse">
            <div className="mb-6 h-6 w-48 rounded bg-cyan-100 dark:bg-cyan-900"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="h-4 w-32 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                  <div className="h-4 w-24 rounded bg-cyan-100 dark:bg-cyan-900"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-4 sm:space-y-6"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent p-2 dark:bg-accent/80">
            <DollarSign className="h-5 w-5 text-bg-card dark:text-bg-main" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Grant Application Analytics
            </h3>
            <p className="text-sm text-gray-900/60">Real-time funding application insights</p>
          </div>
        </div>
        <div className="text-sm text-accent/60">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
        <motion.div
          variants={cardVariants}
          className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="rounded-lg bg-accent/10 p-2 dark:bg-accent/90">
              <FileText className="h-5 w-5 text-accent" />
            </div>
            <span className="text-sm font-medium text-success">+12%</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-accent/60">Total Applications</h3>
            <p className="text-2xl font-bold text-text-primary dark:text-bg-main">
              {analytics.totalApplications.toLocaleString()}
            </p>
            <p className="text-xs text-accent/40">This month</p>
          </div>
        </motion.div>
        <motion.div
          variants={cardVariants}
          className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="rounded-lg bg-success/10 p-2 dark:bg-success/90">
              <CheckCircle className="h-5 w-5 text-success" />
            </div>
            <span className="text-sm font-medium text-success">+8%</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-success/60">Approved</h3>
            <p className="text-2xl font-bold text-text-primary dark:text-bg-main">
              {analytics.approvedApplications.toLocaleString()}
            </p>
            <p className="text-xs text-success/40">{analytics.approvalRate}% approval rate</p>
          </div>
        </motion.div>
        <motion.div
          variants={cardVariants}
          className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="rounded-lg bg-accent/10 p-2 dark:bg-accent/90">
              <Clock className="h-5 w-5 text-accent" />
            </div>
            <span className="text-sm font-medium text-accent/60">
              {analytics.pendingApplications}
            </span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-accent/60">Pending</h3>
            <p className="text-2xl font-bold text-text-primary dark:text-bg-main">
              {analytics.pendingApplications}
            </p>
            <p className="text-xs text-accent/40">Awaiting review</p>
          </div>
        </motion.div>
        <motion.div
          variants={cardVariants}
          className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="rounded-lg bg-accent/10 p-2 dark:bg-accent/90">
              <Award className="h-5 w-5 text-accent" />
            </div>
            <span className="text-sm font-medium text-accent/60">+15%</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-accent/60">Total Approved</h3>
            <p className="text-2xl font-bold text-text-primary dark:text-bg-main">
              P{(analytics.totalAmountApproved / 1000000).toFixed(1)}M
            </p>
            <p className="text-xs text-accent/40">Funds allocated</p>
          </div>
        </motion.div>
      </div>
      <motion.div
        variants={cardVariants}
        className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
      >
        <div className="mb-4 flex items-center justify-between md:mb-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
            Recent Applications
          </h3>
          <button className="text-sm font-medium text-accent hover:text-text-primary dark:text-accent dark:hover:text-bg-main">
            View All
          </button>
        </div>
        <div className="space-y-3 md:space-y-4">
          {recentApplications.map((application, index) => {
            const status = statusConfig[application.status];
            const StatusIcon = status.icon;
            return (
              <motion.div
                key={application.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col gap-3 rounded-lg bg-accent/5 p-3 transition-colors hover:bg-accent/10 dark:bg-accent/90 dark:hover:bg-accent/80 sm:flex-row sm:items-center sm:justify-between md:p-4"
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className={`rounded-lg p-2 ${status.bg}`}>
                    <StatusIcon className={`h-4 w-4 ${status.text}`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 md:text-base">
                      {application.applicant}
                    </h4>
                    <p className="text-xs text-accent/60 md:text-sm">{application.business}</p>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 md:text-base">
                    P {application.amount.toLocaleString()}
                  </p>
                  <p className="text-xs text-accent/60 md:text-sm">
                    {application.processedDate
                      ? `Processed ${new Date(application.processedDate).toLocaleDateString()}`
                      : `Submitted ${new Date(application.submittedDate).toLocaleDateString()}`}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
        <motion.div
          variants={cardVariants}
          className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
        >
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-lg bg-accent/10 p-2 dark:bg-accent/90">
              <Clock className="h-5 w-5 text-accent" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
              Average Processing Time
            </h3>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900 md:text-4xl">
              {analytics.averageProcessingTime}
            </p>
            <p className="text-xs text-accent/60 md:text-sm">days</p>
          </div>
        </motion.div>
        <motion.div
          variants={cardVariants}
          className="rounded-xl border border-accent/20 bg-bg-card p-4 shadow-sm dark:border-accent/40 dark:bg-nav md:p-6"
        >
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-lg bg-success/10 p-2 dark:bg-success/90">
              <TrendingUp className="h-5 w-5 text-success" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
              Approval Rate
            </h3>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900 md:text-4xl">
              {analytics.approvalRate}%
            </p>
            <p className="text-xs text-success/60 md:text-sm">of applications approved</p>
          </div>
        </motion.div>
      </div>
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setDarkMode(dm => !dm)}
          className="rounded-full bg-primary p-3 text-bg-card shadow-lg transition-colors hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/80"
          aria-label="Toggle dark mode"
        >
          {darkMode ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 5.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"
              />
            </svg>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default GrantApplicationAnalytics;
